import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { AuthProvider, useAuth } from './features/auth/AuthContext.jsx';
import { PERMISSIONS } from './lib/permissions.js';
import LoginPage from './features/auth/LoginPage.jsx';
import AppShell from './components/AppShell.jsx';
import { Loading, ToastHost, EmptyState } from './components/ui.jsx';

import DashboardPage from './features/dashboard/DashboardPage.jsx';
import AgentsPage from './features/agents/AgentsPage.jsx';
import AgentFormPage from './features/agents/AgentFormPage.jsx';
import UsersPage from './features/users/UsersPage.jsx';
import UserDetailPage from './features/users/UserDetailPage.jsx';
import BrokersPage from './features/brokers/BrokersPage.jsx';
import BrokerDetailPage from './features/brokers/BrokerDetailPage.jsx';
import InventoryPage from './features/inventory/InventoryPage.jsx';
import ListingDetailPage from './features/inventory/ListingDetailPage.jsx';
import CreditsPage from './features/credits/CreditsPage.jsx';
import BannersPage from './features/banners/BannersPage.jsx';
import SupportPage from './features/support/SupportPage.jsx';
import SupportDetailPage from './features/support/SupportDetailPage.jsx';
import NotificationsPage from './features/notifications/NotificationsPage.jsx';
import ReportsPage from './features/reports/ReportsPage.jsx';
import ReportDetailPage from './features/reports/ReportDetailPage.jsx';
import PaymentsPage from './features/payments/PaymentsPage.jsx';
import ActivityPage from './features/activity/ActivityPage.jsx';
import SettingsPage from './features/settings/SettingsPage.jsx';
import AuditLogPage from './features/audit/AuditLogPage.jsx';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 15_000,
      // A 401 or 403 is a permanent answer; retrying it just delays the truth.
      retry: (failureCount, error) =>
        ![401, 403, 404].includes(error?.status) && failureCount < 2,
    },
    mutations: { retry: false },
  },
});

/**
 * Route guard.
 *
 * Hides a screen an admin cannot use. The server checks the same permission on
 * every request, so this is a courtesy rather than the boundary.
 */
function Protected({ permission, children }) {
  const { canAny } = useAuth();

  // An array means any one of them opens the door — a screen can be reachable
  // by several roles for different reasons.
  const permissions = Array.isArray(permission) ? permission : [permission];

  if (permission && !canAny(...permissions)) {
    return (
      <div style={{ padding: 'var(--space-7)' }}>
        <EmptyState
          title="You do not have access to this"
          body="Your role does not include this permission. Ask a super administrator if you need it."
        />
      </div>
    );
  }

  return children;
}

function Root() {
  const { status } = useAuth();

  if (status === 'loading') {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <Loading label="Restoring your session" />
      </div>
    );
  }

  if (status !== 'signed_in') return <LoginPage />;

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route
          index
          element={
            <Protected permission={PERMISSIONS.DASHBOARD_VIEW}>
              <DashboardPage />
            </Protected>
          }
        />
        <Route
          path="agents"
          element={
            <Protected permission={PERMISSIONS.AGENT_VIEW}>
              <AgentsPage />
            </Protected>
          }
        />
        <Route
          path="agents/new"
          element={
            <Protected permission={PERMISSIONS.AGENT_MANAGE}>
              <AgentFormPage />
            </Protected>
          }
        />
        <Route
          path="agents/:id/edit"
          element={
            <Protected permission={PERMISSIONS.AGENT_MANAGE}>
              <AgentFormPage />
            </Protected>
          }
        />
        <Route
          path="users"
          element={
            <Protected permission={PERMISSIONS.USER_VIEW}>
              <UsersPage />
            </Protected>
          }
        />
        <Route
          path="support"
          element={
            <Protected permission={PERMISSIONS.SUPPORT_VIEW}>
              <SupportPage />
            </Protected>
          }
        />
        <Route
          path="support/:id"
          element={
            <Protected permission={PERMISSIONS.SUPPORT_VIEW}>
              <SupportDetailPage />
            </Protected>
          }
        />
        <Route
          path="reports"
          element={
            <Protected permission={PERMISSIONS.MODERATION_VIEW}>
              <ReportsPage />
            </Protected>
          }
        />
        <Route
          path="reports/:id"
          element={
            <Protected permission={PERMISSIONS.MODERATION_VIEW}>
              <ReportDetailPage />
            </Protected>
          }
        />
        <Route
          path="activity"
          element={
            <Protected permission={PERMISSIONS.LEAD_VIEW}>
              <ActivityPage />
            </Protected>
          }
        />
        <Route
          path="settings"
          element={
            <Protected permission={PERMISSIONS.CONFIG_VIEW}>
              <SettingsPage />
            </Protected>
          }
        />
        <Route
          path="audit-log"
          element={
            <Protected permission={PERMISSIONS.AUDIT_VIEW}>
              <AuditLogPage />
            </Protected>
          }
        />
        <Route
          path="payments"
          element={
            <Protected permission={PERMISSIONS.FINANCE_VIEW}>
              <PaymentsPage />
            </Protected>
          }
        />
        <Route
          path="users/:id"
          element={
            <Protected permission={PERMISSIONS.USER_VIEW}>
              <UserDetailPage />
            </Protected>
          }
        />
        <Route
          path="brokers"
          element={
            <Protected permission={PERMISSIONS.BROKER_VIEW}>
              <BrokersPage />
            </Protected>
          }
        />
        <Route
          path="brokers/:id"
          element={
            <Protected permission={PERMISSIONS.BROKER_VIEW}>
              <BrokerDetailPage />
            </Protected>
          }
        />
        <Route
          path="inventory"
          element={
            <Protected permission={PERMISSIONS.BROKER_PROPERTY_VIEW}>
              <InventoryPage />
            </Protected>
          }
        />
        <Route
          path="inventory/:id"
          element={
            <Protected permission={PERMISSIONS.BROKER_PROPERTY_VIEW}>
              <ListingDetailPage />
            </Protected>
          }
        />
        <Route
          path="credits"
          element={
            <Protected
              permission={[
                PERMISSIONS.FINANCE_VIEW,
                PERMISSIONS.BROKER_PROPERTY_VIEW,
                PERMISSIONS.CONFIG_VIEW,
              ]}
            >
              <CreditsPage />
            </Protected>
          }
        />
        <Route
          path="banners"
          element={
            <Protected permission={PERMISSIONS.CONFIG_VIEW}>
              <BannersPage />
            </Protected>
          }
        />
        <Route
          path="notifications"
          element={
            <Protected permission={PERMISSIONS.NOTIFICATION_SEND}>
              <NotificationsPage />
            </Protected>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Root />
        </BrowserRouter>
        <ToastHost />
      </AuthProvider>
    </QueryClientProvider>
  );
}
